/**
 * TidbitWithSeparator - Renders tidbit text with optional trailing separator
 *
 * Helper component for PlayerIdentityBlockView that displays text with
 * consistent styling and an optional separator for the tidbits section.
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import { type ReactNode } from "react";

import { Text } from "@/components/atoms/Text";

import TidbitSeparator from "./TidbitSeparator";

/**
 * Returns the shared typography class name for tidbit text elements
 * @param isDark - Whether dark mode is enabled
 * @returns CSS class names for tidbit text styling
 */
export function getTidbitTextClassName(isDark: boolean): string {
  return `font-body text-body ${isDark ? "text-content-subtle" : "text-content-neutral-strong"}`;
}

/**
 * Formats age display text with optional birth year
 * @param age - Player's age in years
 * @param birthYear - Player's birth year (optional)
 * @returns Formatted age string like "25 years old" or "25 years old (1998)"
 */
export function formatAgeDisplay(
  age: string,
  birthYear: string | null
): string {
  return birthYear ? `${age} years old (${birthYear})` : `${age} years old`;
}

/** Props for TidbitWithSeparator component */
type TidbitWithSeparatorProps = {
  /** Whether dark mode is enabled, affects text and separator colors */
  readonly isDark: boolean;
  /** Content to display in the tidbit text */
  readonly children: ReactNode;
  /** Whether to show the trailing separator (defaults to true) */
  readonly showSeparator?: boolean;
  /** Optional testID applied to the underlying Text element */
  readonly testID?: string;
};

/**
 * TidbitWithSeparator renders tidbit text with optional trailing separator
 * @param props - Component props
 * @param props.isDark - Whether dark mode is enabled
 * @param props.children - Content to display
 * @param props.showSeparator - Whether to show trailing separator
 * @param props.testID - Optional testID applied to the tidbit Text element
 * @returns Fragment containing Text and optional TidbitSeparator
 */
export default function TidbitWithSeparator({
  isDark,
  children,
  showSeparator = true,
  testID,
}: TidbitWithSeparatorProps): ReactNode {
  return (
    <>
      <Text
        variant="unstyled"
        {...(testID ? { testID, "data-testid": testID } : {})}
        className={getTidbitTextClassName(isDark)}
      >
        {children}
      </Text>
      {showSeparator ? <TidbitSeparator isDark={isDark} /> : null}
    </>
  );
}
